import PageHero from '@/components/PageHero'
import PageCTA from '@/components/PageCTA'
import SEO from '@/components/SEO'
import { apropos, images, links } from '@/content/site'

function APropos() {
  return (
    <div>
      <SEO
        title="Alexandra Goris — Accompagnante en transformation par l'art | HeartCanvas"
        description="Diplômée en art-thérapie relationnelle, artiste professionnelle depuis plus de 20 ans. Fondatrice de HeartCanvas en Suisse."
        path="/a-propos"
      />
      {/* PHRASE D'ENTRÉE */}
      <PageHero
        eyebrow={apropos.hero.eyebrow}
        title={apropos.hero.title}
      />
      <div className="text-center -mt-10 mb-4">
        <p className="font-serif-display text-[14px] tracking-[0.15em] text-[#3A3236] italic">Alexandra Goris</p>
      </div>

      {/* MON HISTOIRE */}
      <section className="px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <img src="https://static.lumi.new/material/4f/4f74b43a2f16bd90f3c5e3b3bbc6185a.jpg" alt="Portrait d'Alexandra Goris, fondatrice de HeartCanvas — formée en art-thérapie relationnelle (Paris)" loading="lazy" className="w-full aspect-[4/5] object-cover sticky top-32" />
          </div>
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-8">{apropos.monHistoire.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] mb-10">
              {apropos.monHistoire.title}
            </h2>
            <div className="space-y-7 font-serif-display text-[18px] md:text-[20px] leading-[1.6] text-[#1F1B1D]">
              {apropos.monHistoire.paragraphs.map((p, i) => (
                <p key={i} className={i === apropos.monHistoire.paragraphs.length - 1 ? 'text-[#3A3236]' : ''}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CE QUE ÇA M'A APPRIS */}
      <section className="bg-[#EFE5EC] border-y border-[#DCC9D2] px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{apropos.appris.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D]">
              {apropos.appris.title}
            </h2>
          </div>
          <div className="md:col-span-8 md:pt-4">
            <ul className="space-y-6">
              {apropos.appris.items.map((line, i) => (
                <li key={i} className="flex gap-5 items-start">
                  <span className="font-serif-display italic text-[#C0923F] text-lg w-8 shrink-0 mt-1">0{i + 1}</span>
                  <p className="font-serif-display text-[18px] md:text-[20px] leading-[1.55] text-[#1F1B1D]">{line}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FORMATION ET EXPÉRIENCE */}
      <section className="px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{apropos.formation.eyebrow}</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D]">
                {apropos.formation.title}
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#DCC9D2] border border-[#DCC9D2]">
            {apropos.formation.items.map(it => (
              <div key={it.title} className="bg-[#F6EFF4] p-8 md:p-10">
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#C0923F] mb-3">{it.place}</div>
                <h3 className="font-serif-display text-[22px] md:text-[26px] text-[#1F1B1D] mb-4 leading-tight">{it.title}</h3>
                <p className="text-[15px] leading-[1.7] text-[#3A3236]">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MON UNIVERS ARTISTIQUE */}
      <section className="bg-[#EFE5EC] border-y border-[#DCC9D2] px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7 md:order-2">
            <img src="https://static.lumi.new/material/7d/7d2c83030e5d0bdde93f64bd7509a7e5.jpg" alt="Univers artistique d'Alexandra Goris — œuvres et inspiration HeartCanvas" loading="lazy" className="w-full aspect-[4/3] object-cover" />
          </div>
          <div className="md:col-span-5 md:order-1">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{apropos.univers.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] mb-8">
              {apropos.univers.title}
            </h2>
            <p className="font-serif-display italic text-[18px] md:text-[20px] leading-[1.55] text-[#3A3236]">
              {apropos.univers.body} <span className="text-[#1F1B1D] not-italic">{apropos.univers.artistName}</span>.
            </p>
            <a
              href={links.amanaliya}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-block link-underline text-[13px] tracking-[0.2em] uppercase text-[#9E3A5A]"
            >
              {apropos.univers.linkLabel}
            </a>
          </div>
        </div>
      </section>

      {/* PHRASE FINALE */}
      <section className="bg-[#F6EFF4] border-y border-[#DCC9D2] px-6 md:px-10 py-32 md:py-44">
        <div className="max-w-5xl mx-auto text-center">
          <img
            src="https://static.lumi.new/material/ab/ab8e81f64d56c45dce271e793807b71f.jpeg"
            alt="L'art ne s'explique pas, il révèle — détail d'une œuvre HeartCanvas"
            loading="lazy"
            className="w-32 h-32 md:w-40 md:h-40 object-cover mx-auto mb-12"
          />
          <p className="font-serif-display italic text-3xl md:text-5xl leading-[1.3] text-[#2C2C2C]">
            {apropos.phraseFinale.line1}<br />
            <span className="text-[#9E3A5A]">{apropos.phraseFinale.line2Highlight}</span><br />
            {apropos.phraseFinale.line3}
          </p>
        </div>
      </section>

      <PageCTA
        eyebrow={apropos.invitationFinale.eyebrow}
        title={apropos.invitationFinale.title}
      />
    </div>
  )
}

export default APropos
